<template>
  <footer class="bg-[#3B2314]">
    <div class="flex flex-col gap-11 items-center px-6 py-12 sm:px-10 sm:py-16 lg:px-10">
      <a
        :href="logoHref"
        class="block"
        aria-label="Go to Lifeboost Coffee homepage"
      >
        <nuxt-img
          :src="logoSrc"
          :alt="logoAlt"
          class="w-40 h-auto"
          loading="lazy"
        />
      </a>

      <div class="flex flex-wrap gap-4 justify-center items-start">
        <a
          :href="phoneLink"
          class="inline-flex gap-2 items-center px-6 py-2.5 rounded-2xl bg-white/[0.04] text-[#C8A882] transition-opacity hover:opacity-80"
        >
          <svg
            class="shrink-0 size-5"
            viewBox="0 0 19 19"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M2.0129 1.87014H6.21129L7.56431 5.25324L5.39031 6.7026C5.26229 6.78802 5.15733 6.90371 5.08474 7.03943C5.01215 7.17514 4.97419 7.32667 4.97421 7.48058C4.97702 7.56847 4.97421 7.48151 4.97421 7.48151V7.50115C4.97468 7.54297 4.97655 7.58476 4.97982 7.62645C4.98481 7.70375 4.99572 7.80661 5.01255 7.93502C5.04902 8.18749 5.11915 8.53534 5.25566 8.9449C5.53057 9.76776 6.06916 10.8328 7.11829 11.882C8.16742 12.9311 9.23244 13.4697 10.0544 13.7446C10.4648 13.8811 10.8117 13.9503 11.0661 13.9877C11.2095 14.0086 11.3541 14.0211 11.499 14.0251L11.5112 14.0261H11.5186C11.5186 14.0261 11.6234 14.0205 11.5196 14.0261C11.6932 14.026 11.8633 13.9776 12.011 13.8862C12.1586 13.7949 12.2779 13.6643 12.3555 13.509L12.982 12.256L17.1299 12.9479V16.9875C15.156 17.2727 9.82433 17.5541 5.63529 13.365C1.44626 9.17586 1.72677 3.84315 2.0129 1.87014ZM6.91258 7.93502L8.60222 6.8092C8.95917 6.5711 9.22339 6.2175 9.35057 5.8077C9.47774 5.3979 9.46014 4.95683 9.3007 4.55848L7.94768 1.17539C7.80884 0.828385 7.56921 0.530948 7.2597 0.321442C6.9502 0.111937 6.58503 -2.50538e-05 6.21129 4.20523e-09H1.96428C1.11432 4.20523e-09 0.314846 0.590031 0.175523 1.51201C-0.142395 3.60844 -0.573454 9.80049 4.31313 14.6872C9.19971 19.5739 15.3916 19.1419 17.488 18.8249C18.41 18.6846 19 17.8861 19 17.0361V12.9479C19.0001 12.5052 18.8431 12.0769 18.557 11.7391C18.2709 11.4012 17.8742 11.1759 17.4375 11.103L13.2896 10.412C12.8951 10.3462 12.4899 10.4085 12.1335 10.59C11.777 10.7714 11.4881 11.0623 11.3092 11.42L10.9857 12.068C10.8713 12.0396 10.7581 12.0069 10.6462 11.9699C10.0665 11.7772 9.26143 11.3808 8.44045 10.5598C7.61948 9.73878 7.22301 8.93368 7.03039 8.353C6.98498 8.21556 6.94598 8.07608 6.91351 7.93502H6.91258Z"
              fill="currentColor"
            />
          </svg>
          <span class="text-base whitespace-nowrap leading-30 font-ww-regular">{{ phoneNumber }}</span>
        </a>

        <button
          type="button"
          class="inline-flex gap-2 items-center px-6 py-2.5 rounded-2xl bg-white/[0.04] text-[#C8A882] transition-opacity hover:opacity-80"
          aria-label="Open live chat"
          @click="openLiveChat"
        >
          <svg
            class="shrink-0 size-4"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M1 9C1 6.87827 1.84285 4.84344 3.34315 3.34315C4.84344 1.84285 6.87827 1 9 1C11.1217 1 13.1566 1.84285 14.6569 3.34315C16.1571 4.84344 17 6.87827 17 9V14.09C17 14.938 17 15.36 16.874 15.699C16.7738 15.9676 16.617 16.2116 16.4143 16.4143C16.2116 16.617 15.9676 16.7738 15.699 16.874C15.36 17 14.937 17 14.09 17H9C6.87827 17 4.84344 16.1571 3.34315 14.6569C1.84285 13.1566 1 11.1217 1 9Z"
              stroke="currentColor"
              stroke-width="2"
            />
            <path
              d="M6 8H12M9 12H12"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span class="text-base whitespace-nowrap leading-30 font-ww-regular">{{ liveChatText }}</span>
        </button>

        <a
          :href="emailLink"
          class="inline-flex gap-2 items-center px-6 py-2.5 rounded-2xl bg-white/[0.04] text-[#C8A882] transition-opacity hover:opacity-80"
        >
          <svg
            class="w-5 h-4 shrink-0"
            viewBox="0 0 20 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M17 0H3C2.20435 0 1.44129 0.316071 0.87868 0.87868C0.316071 1.44129 0 2.20435 0 3V13C0 13.7956 0.316071 14.5587 0.87868 15.1213C1.44129 15.6839 2.20435 16 3 16H17C17.7956 16 18.5587 15.6839 19.1213 15.1213C19.6839 14.5587 20 13.7956 20 13V3C20 2.20435 19.6839 1.44129 19.1213 0.87868C18.5587 0.316071 17.7956 0 17 0ZM16.33 2L10 6.75L3.67 2H16.33ZM17 14H3C2.73478 14 2.48043 13.8946 2.29289 13.7071C2.10536 13.5196 2 13.2652 2 13V3.25L9.4 8.8C9.5731 8.92982 9.78363 9 10 9C10.2164 9 10.4269 8.92982 10.6 8.8L18 3.25V13C18 13.2652 17.8946 13.5196 17.7071 13.7071C17.5196 13.8946 17.2652 14 17 14Z"
              fill="currentColor"
            />
          </svg>
          <span class="text-base whitespace-nowrap leading-30 font-ww-regular">{{ email }}</span>
        </a>
      </div>

      <div class="flex flex-col gap-4 items-center w-full max-w-6xl">
        <p
          v-if="disclaimer"
          class="text-center text-13 leading-6 text-[#C8A882] font-ww-regular"
          v-html="disclaimer"
        />

        <p class="text-xs leading-5 text-center font-ww-regular">
          <span class="text-[#8A7060]">© {{ copyrightYear }} {{ copyrightText }} | </span>
          <a
            :href="privacyUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="text-[#D4B89A] hover:underline"
          >{{ privacyText }}</a>
          <span class="text-[#8A7060]"> | </span>
          <a
            :href="termsUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="text-[#D4B89A] hover:underline"
          >{{ termsText }}</a>
        </p>
      </div>
    </div>
  </footer>
</template>

<script setup>
defineProps({
  logoSrc: {
    type: String,
    default: '/assets/images/logo-footer.svg'
  },
  logoAlt: {
    type: String,
    default: 'Lifeboost Coffee'
  },
  logoHref: {
    type: String,
    default: 'https://lifeboostcoffee.com/'
  },
  phoneNumber: {
    type: String,
    default: '1-800-479-1596'
  },
  phoneLink: {
    type: String,
    default: 'tel:1-800-479-1596'
  },
  liveChatText: {
    type: String,
    default: 'Live Chat'
  },
  email: {
    type: String,
    default: 'support@lifeboostcoffee.com'
  },
  emailLink: {
    type: String,
    default: 'mailto:support@lifeboostcoffee.com'
  },
  disclaimer: {
    type: String,
    default:
      'This article is a paid advertorial partnership with Lifeboost Coffee. The statements in this article have not been evaluated by the Food and Drug Administration. These products are not intended to diagnose, treat, cure, or prevent any disease. Individual results may vary. Consult your healthcare provider before starting any new supplement regimen.'
  },
  copyrightYear: {
    type: [String, Number],
    default: 2026
  },
  copyrightText: {
    type: String,
    default: 'Lifeboost Coffee. All rights reserved.'
  },
  privacyText: {
    type: String,
    default: 'Privacy Policy'
  },
  privacyUrl: {
    type: String,
    default: 'https://lifeboostcoffee.com/pages/privacy-policy'
  },
  termsText: {
    type: String,
    default: 'Terms of Service'
  },
  termsUrl: {
    type: String,
    default: 'https://lifeboostcoffee.com/pages/terms-conditions'
  }
})

const openLiveChat = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  const gorgias = window.GorgiasChat
  if (gorgias && typeof gorgias.open === 'function') {
    gorgias.open()
    return
  }

  const iframe = document.querySelector('iframe#chat-button')
  const iframeDoc = iframe?.contentDocument || iframe?.contentWindow?.document
  const btn = iframeDoc?.getElementById('gorgias-chat-messenger-button')
  if (btn && typeof btn.click === 'function') {
    btn.click()
  }
}

defineBuilderComponent({
  friendlyName: 'Content - Article Footer',
  inputs: [
    {
      name: 'logoSrc',
      type: 'file',
      defaultValue: '/assets/images/logo-footer.svg',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'svg']
    },
    {
      name: 'logoAlt',
      type: 'text',
      defaultValue: 'Lifeboost Coffee'
    },
    {
      name: 'logoHref',
      type: 'url',
      defaultValue: 'https://lifeboostcoffee.com/'
    },
    {
      name: 'phoneNumber',
      type: 'text',
      defaultValue: '1-800-479-1596'
    },
    {
      name: 'phoneLink',
      type: 'text',
      defaultValue: 'tel:1-800-479-1596'
    },
    {
      name: 'liveChatText',
      type: 'text',
      defaultValue: 'Live Chat'
    },
    {
      name: 'email',
      type: 'text',
      defaultValue: 'support@lifeboostcoffee.com'
    },
    {
      name: 'emailLink',
      type: 'text',
      defaultValue: 'mailto:support@lifeboostcoffee.com'
    },
    {
      name: 'disclaimer',
      type: 'richText',
      defaultValue:
        'This article is a paid advertorial partnership with Lifeboost Coffee. The statements in this article have not been evaluated by the Food and Drug Administration. These products are not intended to diagnose, treat, cure, or prevent any disease. Individual results may vary. Consult your healthcare provider before starting any new supplement regimen.'
    },
    {
      name: 'copyrightYear',
      type: 'number',
      defaultValue: 2026
    },
    {
      name: 'copyrightText',
      type: 'text',
      defaultValue: 'Lifeboost Coffee. All rights reserved.'
    },
    {
      name: 'privacyText',
      type: 'text',
      defaultValue: 'Privacy Policy'
    },
    {
      name: 'privacyUrl',
      type: 'url',
      defaultValue: 'https://lifeboostcoffee.com/pages/privacy-policy'
    },
    {
      name: 'termsText',
      type: 'text',
      defaultValue: 'Terms of Service'
    },
    {
      name: 'termsUrl',
      type: 'url',
      defaultValue: 'https://lifeboostcoffee.com/pages/terms-conditions'
    }
  ]
})
</script>
